import { AppContext } from 'contexts/app-context';
import React, { useContext, VFC } from 'react';
import Highlighter from 'react-highlight-words';
import { useParams } from 'react-router-dom';
import { RouteParams } from 'types';

import { useJobsSearch } from 'hooks/useJobsSearch';
import { useJobApplication } from 'hooks/v2/useJobApplication';
import { usePositionsCache } from 'hooks/usePositionsCache';

import { Skeleton } from './ui/skeleton';

export function SearchPopoverSkeleton() {
  return (
    <div className="rounded-6 absolute mt-1 h-[236px] w-full cursor-default overflow-y-scroll border border-gray-100 bg-white pt-2 shadow-lg">
      <p className="text-12 font-700 bg-gray-200 px-4 py-[6px] uppercase leading-4">
        jobs
      </p>
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={`skeleton-${i}`}
          className="flex h-[60px] w-full flex-col gap-1 px-4 py-3 text-start"
        >
          <Skeleton className="h-[18px] w-[200px]" />
          <div className="flex h-4 items-center gap-1">
            <Skeleton className="h-4 w-[150px]" />
            <p className="text-12 text-gray-300">|</p>
            <Skeleton className="h-4 w-[150px]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface SearchPopoverProps {
  searchString: string;
  setOpenPopover: React.Dispatch<React.SetStateAction<boolean>>;
  scrollToJobs: () => void;
  resetSearch: () => void;
}

export const SearchPopover: VFC<SearchPopoverProps> = ({
  searchString,
  setOpenPopover,
  scrollToJobs,
  resetSearch,
}) => {
  const {
    setPosition,
    setCategoryName,
    resetFilters,
    setIsJobsFromCurrentLocation,
    setPlaceId,
    updateUrlWithParams,
    setActiveFilters,
    setExperienceLevelName,
    setJobSearchQuery,
    setPage,
  } = useContext(AppContext);
  const { accountSlug, brandId } = useParams<RouteParams>();
  const { addPositionToCache } = usePositionsCache();
  const { handleApplyClick } = useJobApplication();

  const { data, isFetching } = useJobsSearch(
    accountSlug,
    brandId,
    searchString,
  );

  const handleSelect = (type: string, id: string, value: string) => {
    setOpenPopover(false);
    setCategoryName(null);
    resetSearch();

    if (type === 'jobs') {
      handleApplyClick(id);
    } else if (type === 'positions') {
      // Add the position to the cache so it's available in the dropdown
      if (accountSlug && brandId) {
        addPositionToCache(accountSlug, brandId, { id, name: value });
      }

      // Don't reset filters when selecting a position - just update the position
      setPosition({ id, value });
      setCategoryName(null);
      setExperienceLevelName(null);
      setPlaceId(null);
      setJobSearchQuery(null);
      setPage(1);
      setIsJobsFromCurrentLocation(false);

      // Update the active filters to show the selected position
      setActiveFilters(prev => ({
        ...prev,
        position: {
          key: id,
          label: value,
        },
      }));

      scrollToJobs();
      updateUrlWithParams(accountSlug, brandId, 'position', id);
    } else if (type === 'locations') {
      resetFilters();
      setIsJobsFromCurrentLocation(true);
      setPlaceId(id);
      scrollToJobs();
      updateUrlWithParams(accountSlug, brandId, 'place_id', id);
    }
  };

  if (isFetching) {
    return <SearchPopoverSkeleton />;
  }

  return (
    <>
      {data && (
        <div className="rounded-6 absolute mt-1 max-h-[236px] w-full cursor-default overflow-y-scroll border border-gray-100 bg-white pt-2 shadow-lg">
          {Object.entries(data).map(([type, values]) => (
            <span key={type}>
              {values && values.length > 0 && (
                <p className="text-12 font-700 bg-gray-200 px-4 py-[6px] uppercase leading-4">
                  {type}
                </p>
              )}
              {values.map(item => (
                <div key={item.external_id}>
                  <button
                    type="button"
                    onClick={() =>
                      handleSelect(type, item.external_id, item.name)
                    }
                    className="text-14 font-400 hover:bg-primary-50 flex w-full flex-col gap-1 px-4 py-3 text-start leading-4 text-gray-800"
                  >
                    <Highlighter
                      autoEscape
                      searchWords={[searchString]}
                      textToHighlight={item.name}
                      highlightClassName="text-primary"
                      highlightStyle={{
                        // Ripple's `amber-100`, as a literal. This package
                        // never imports `@fountain/ripple/styles`, so a
                        // `var(--ds-color-*)` here would resolve to its
                        // fallback forever while reading as bridged.
                        background: '#fef3c7',
                      }}
                    />
                    {type === 'jobs' && (
                      <p className="text-12 text-gray-500">
                        {item.location} &nbsp;|&nbsp; {item.position}
                      </p>
                    )}
                  </button>
                </div>
              ))}
            </span>
          ))}
        </div>
      )}
    </>
  );
};
